import { computeSpaces, spaceContains } from './spaces.jsx';
import { spaceEdges, edgeLoops } from '../element3D/spacePieces.jsx';
import { rayToWall, polyArea, pointInPoly, simplifyClosed } from './zoneSplit.jsx';

// «رنگ کردنِ خانه‌ها»: هر فضا به خانه‌های درشت تقسیم می‌شود — خط‌ها همان امتدادِ سرِ دیوارها و گوشه‌ها هستند
// (جایی که مرزِ واقعی معمولاً همان‌جاست) و فاصله‌های بزرگ با خانه‌های حدوداً یک‌متری پر می‌شوند. کاربر اسمِ قسمت
// (رنگ) را انتخاب می‌کند و روی خانه‌ها انگشت می‌کشد. هر جا دو رنگ (یا رنگ و بی‌رنگ) به هم رسیدند همان‌جا مرز است:
// مرزها همیشه صاف، در امتدادِ دیوارها و بسته‌اند. در پایان هر تکه‌ی هم‌رنگ یک «قسمت» (room.zone) می‌شود — همان
// مدلی که ابزارِ دستی می‌سازد، پس دستگیره‌های ↔ برای تنظیمِ ریز همچنان کار می‌کنند.
// مختصات سانتی‌متر.

export const PAINT_PRESETS = [
  { name: 'پذیرایی', color: '#3b82f6' },
  { name: 'آشپزخانه', color: '#f59e0b' },
  { name: 'ناهارخوری', color: '#10b981' },
  { name: 'نشیمن', color: '#8b5cf6' },
  { name: 'راهرو', color: '#64748b' },
];
const EXTRA_COLORS = ['#ef4444', '#14b8a6', '#ec4899', '#84cc16', '#0ea5e9', '#a855f7', '#d97706', '#0891b2'];

export function paintColor(name, names) {
  const p = PAINT_PRESETS.find((q) => q.name === name);
  if (p) return p.color;
  const extras = names.filter((n) => !PAINT_PRESETS.some((q) => q.name === n));
  const k = Math.max(0, extras.indexOf(name));
  return EXTRA_COLORS[k % EXTRA_COLORS.length];
}

// اندازه‌ی تقریبیِ خانه‌ها در فاصله‌های بی‌نشانه (سانتی‌متر) — کاربر از نوارِ پایین عوضش می‌کند
export const PAINT_STEPS = [50, 75, 100, 150, 200];
const MIN_GAP = 35; // خط‌های نزدیک‌تر از این یکی می‌شوند
const EDGE = 30; // خط‌های چسبیده به لبه‌ی فضا (ضخامتِ دیوار) حذف
const MIN_SPACE = 6000; // فضاهای ریزتر از ۰٫۶ متر مربع خانه نمی‌گیرند

const withIds = (ws) => ws.map((w, i) => ({ id: w.id ?? `t${i}`, ...w }));
const piecesOf = (sp) => (sp.rects && sp.rects.length ? sp.rects : [sp]);

function bboxOf(sp) {
  let x1 = Infinity;
  let y1 = Infinity;
  let x2 = -Infinity;
  let y2 = -Infinity;
  piecesOf(sp).forEach((q) => {
    const xs = q.xl0 !== undefined ? [q.xl0, q.xl1, q.xr0, q.xr1] : [q.x1, q.x2];
    x1 = Math.min(x1, ...xs);
    x2 = Math.max(x2, ...xs);
    y1 = Math.min(y1, q.y1, q.y2);
    y2 = Math.max(y2, q.y1, q.y2);
  });
  return { x1, y1, x2, y2 };
}

function axisLines(lo, hi, cands, STEP) {
  const inner = cands.filter((v) => v > lo + EDGE && v < hi - EDGE).sort((a, b) => a - b);
  const merged = [];
  inner.forEach((v) => {
    if (!merged.length || v - merged[merged.length - 1] >= MIN_GAP) merged.push(v);
  });
  const pts = [lo, ...merged, hi];
  const out = [lo];
  for (let k = 1; k < pts.length; k += 1) {
    const a = pts[k - 1];
    const b = pts[k];
    const n = Math.max(1, Math.round((b - a) / STEP));
    for (let m = 1; m < n; m += 1) out.push(a + ((b - a) * m) / n);
    out.push(b);
  }
  return out;
}

// خانه‌بندیِ همه‌ی فضاها + رنگِ اولیه از «قسمت»ها و اتاق‌های اسم‌دارِ فعلی
// baseWalls: دیوارها بدونِ مرزهای «قسمت» (zoneOf)
export function buildPaintModel(baseWalls, rooms, step = 100) {
  const spaces = computeSpaces(withIds(baseWalls), [], [], []).filter((sp) => sp.area >= MIN_SPACE);
  const labels = {};
  const model = spaces.map((sp, s) => {
    const bb = bboxOf(sp);
    const ex = 40;
    const cx = [];
    const cy = [];
    baseWalls.forEach((w) => {
      [
        [w.x1, w.y1],
        [w.x2, w.y2],
      ].forEach(([x, y]) => {
        if (x >= bb.x1 - ex && x <= bb.x2 + ex && y >= bb.y1 - ex && y <= bb.y2 + ex) {
          cx.push(x);
          cy.push(y);
        }
      });
    });
    const zoneRooms = rooms.filter((r) => r.zone && r.zone.poly && r.zone.P && spaceContains(sp, r.zone.P.x, r.zone.P.y, 2));
    zoneRooms.forEach((r) =>
      r.zone.poly.forEach(([x, y]) => {
        cx.push(x);
        cy.push(y);
      })
    );
    const plain = rooms.find((r) => !r.zone && r.centroid && spaceContains(sp, r.centroid.x, r.centroid.y, 2)) || null;
    const xs = axisLines(bb.x1, bb.x2, cx, step);
    const ys = axisLines(bb.y1, bb.y2, cy, step);
    const grid = [];
    const cells = [];
    for (let i = 0; i < xs.length - 1; i += 1) {
      grid.push([]);
      for (let j = 0; j < ys.length - 1; j += 1) {
        const x1 = xs[i];
        const x2 = xs[i + 1];
        const y1 = ys[j];
        const y2 = ys[j + 1];
        let n = 0;
        let px = null;
        let py = null;
        const mx = (x1 + x2) / 2;
        const my = (y1 + y2) / 2;
        if (spaceContains(sp, mx, my, 0)) {
          px = mx;
          py = my;
        }
        for (let a = 0; a < 4; a += 1) {
          for (let b = 0; b < 4; b += 1) {
            const x = x1 + ((a + 0.5) / 4) * (x2 - x1);
            const y = y1 + ((b + 0.5) / 4) * (y2 - y1);
            if (spaceContains(sp, x, y, 0)) {
              n += 1;
              if (px == null) {
                px = x;
                py = y;
              }
            }
          }
        }
        if (n < 1) {
          grid[i].push(null);
          continue;
        }
        // tiny: تکه‌ی باریکِ کنارِ دیوارِ مورب/منحنی — با خانه‌ی کناری‌اش رنگ می‌شود
        const cell = { id: `${s}:${i}:${j}`, s, i, j, x1, y1, x2, y2, px, py, area: ((x2 - x1) * (y2 - y1) * n) / 16, tiny: n < 5 };
        const z = zoneRooms.find((r) => pointInPoly(r.zone.poly, px, py));
        const name = z ? z.name : plain ? plain.name : null;
        if (name) labels[cell.id] = name;
        grid[i].push(cell);
        cells.push(cell);
      }
    }
    return { s, sp, bb, xs, ys, grid, cells, zoneRoomIds: zoneRooms.map((r) => r.id), plainRoomId: plain ? plain.id : null };
  });
  return { spaces: model.filter((m) => m.cells.length), labels };
}

export function findPaintCell(model, x, y) {
  for (const m of model.spaces) {
    if (x < m.bb.x1 || x > m.bb.x2 || y < m.bb.y1 || y > m.bb.y2) continue;
    if (!spaceContains(m.sp, x, y, 3)) continue;
    let i = m.xs.findIndex((v, k) => k > 0 && x <= v) - 1;
    let j = m.ys.findIndex((v, k) => k > 0 && y <= v) - 1;
    if (i < 0) i = 0;
    if (j < 0) j = 0;
    const c = m.grid[i] && m.grid[i][j];
    if (c) return c;
  }
  return null;
}

// خانه‌هایی که با یک لمس رنگ می‌شوند: خودش + تکه‌های باریکِ (tiny) کنارش
export function paintTargets(model, cell) {
  const m = model.spaces.find((q) => q.s === cell.s);
  const out = [cell];
  if (!m) return out;
  [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ].forEach(([di, dj]) => {
    const q = m.grid[cell.i + di] && m.grid[cell.i + di][cell.j + dj];
    if (q && q.tiny) out.push(q);
  });
  return out;
}

// تکه‌های هم‌رنگِ به‌هم‌چسبیده (هر فضا جدا)
export function paintComponents(model, labels) {
  const out = [];
  model.spaces.forEach((m) => {
    const seen = new Set();
    m.cells.forEach((c0) => {
      const name = labels[c0.id];
      if (!name || seen.has(c0.id)) return;
      const cells = [];
      const st = [c0];
      seen.add(c0.id);
      while (st.length) {
        const c = st.pop();
        cells.push(c);
        [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ].forEach(([di, dj]) => {
          const q = m.grid[c.i + di] && m.grid[c.i + di][c.j + dj];
          if (q && !seen.has(q.id) && labels[q.id] === name) {
            seen.add(q.id);
            st.push(q);
          }
        });
      }
      const area = cells.reduce((a, c) => a + c.area, 0);
      const gx = cells.reduce((a, c) => a + c.px * c.area, 0) / area;
      const gy = cells.reduce((a, c) => a + c.py * c.area, 0) / area;
      const at = cells.reduce((b, c) => (Math.hypot(c.px - gx, c.py - gy) < Math.hypot(b.px - gx, b.py - gy) ? c : b));
      out.push({ s: m.s, name, cells, area, at });
    });
  });
  return out;
}

// مرزهای بینِ خانه‌هایی که رنگشان فرق دارد (برای کشیدن) — [{x1,y1,x2,y2}]
export function paintBoundaries(m, labels) {
  const segs = [];
  for (let i = 0; i < m.grid.length; i += 1) {
    for (let j = 0; j < m.grid[i].length; j += 1) {
      const c = m.grid[i][j];
      if (!c) continue;
      const r = m.grid[i + 1] && m.grid[i + 1][j];
      const at = (a, b) => (m.grid[a] && m.grid[a][b]) || null;
      if (r && (labels[c.id] || null) !== (labels[r.id] || null))
        segs.push({ x1: c.x2, y1: c.y1, x2: c.x2, y2: c.y2, v: true, openLo: !at(i, j - 1) && !at(i + 1, j - 1), openHi: !at(i, j + 1) && !at(i + 1, j + 1) });
      const d = m.grid[i][j + 1];
      if (d && (labels[c.id] || null) !== (labels[d.id] || null))
        segs.push({ x1: c.x1, y1: c.y2, x2: c.x2, y2: c.y2, v: false, openLo: !at(i - 1, j) && !at(i - 1, j + 1), openHi: !at(i + 1, j) && !at(i + 1, j + 1) });
    }
  }
  return segs;
}

// مرز را تا دیوار کوتاه می‌کند (کنارِ دیوارِ مورب/منحنی از فضا بیرون نزند) و تکه‌های هم‌خط را یکی می‌کند
function trimAndMerge(segs, m, baseWalls) {
  const trimmed = [];
  segs.forEach((g) => {
    const along = g.v ? 'y' : 'x';
    const lo = g.v ? g.y1 : g.x1;
    const hi = g.v ? g.y2 : g.x2;
    const fixed = g.v ? g.x1 : g.y1;
    let mid = null;
    for (const t of [0.5, 0.3, 0.7, 0.15, 0.85]) {
      const u = lo + (hi - lo) * t;
      const [x, y] = g.v ? [fixed, u] : [u, fixed];
      if (spaceContains(m.sp, x, y, 1)) {
        mid = u;
        break;
      }
    }
    if (mid == null) return;
    const [mx, my] = g.v ? [fixed, mid] : [mid, fixed];
    const back = rayToWall(baseWalls, mx, my, along, -1);
    const fwd = rayToWall(baseWalls, mx, my, along, 1);
    // سرِ مرز که به لبه‌ی فضا رسیده (خانه‌ی بعدی نیست) تا خودِ دیوار ادامه پیدا می‌کند تا فضا درز نداشته باشد
    const a = back != null ? Math.max(g.openLo ? mid - 150 : lo, mid - back - 3) : lo;
    const b = fwd != null ? Math.min(g.openHi ? mid + 150 : hi, mid + fwd + 3) : hi;
    if (b - a < 1) return;
    trimmed.push({ v: g.v, c: fixed, a, b });
  });
  const groups = new Map();
  trimmed.forEach((t) => {
    const k = `${t.v ? 'v' : 'h'}${Math.round(t.c * 10)}`;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(t);
  });
  const out = [];
  groups.forEach((list) => {
    list.sort((p, q) => p.a - q.a);
    let cur = { ...list[0] };
    for (let k = 1; k < list.length; k += 1) {
      const t = list[k];
      if (t.a <= cur.b + 0.5) cur.b = Math.max(cur.b, t.b);
      else {
        out.push(cur);
        cur = { ...t };
      }
    }
    out.push(cur);
  });
  return out.map((t) => (t.v ? { x1: t.c, y1: t.a, x2: t.c, y2: t.b } : { x1: t.a, y1: t.c, x2: t.b, y2: t.c }));
}

// نتیجه‌ی رنگ‌آمیزی → تغییراتِ اتاق‌ها. فقط فضاهایی که رنگشان عوض شد دوباره ساخته می‌شوند (بقیه دست‌نخورده).
// خروجی: { removeRoomIds, renames: [{id, name}], plains: [{name, P}], zones: [{name, poly, P}] }
export function paintResult(model, labels0, labels, baseWalls) {
  const res = { removeRoomIds: [], renames: [], plains: [], zones: [] };
  const comps = paintComponents(model, labels);
  model.spaces.forEach((m) => {
    const dirty = m.cells.some((c) => (labels0[c.id] || null) !== (labels[c.id] || null));
    if (!dirty) return;
    const mine = comps.filter((q) => q.s === m.s);
    res.removeRoomIds.push(...m.zoneRoomIds);
    // کلِ فضا یک رنگ → اتاقِ معمولی (بی‌مرز)
    if (mine.length === 1 && mine[0].cells.length === m.cells.length) {
      if (m.plainRoomId != null) res.renames.push({ id: m.plainRoomId, name: mine[0].name });
      else res.plains.push({ name: mine[0].name, P: { x: mine[0].at.px, y: mine[0].at.py } });
      return;
    }
    if (m.plainRoomId != null) res.removeRoomIds.push(m.plainRoomId);
    if (!mine.length) return;
    const segs = trimAndMerge(paintBoundaries(m, labels), m, baseWalls);
    const tmp = withIds([...baseWalls, ...segs.map((g, k) => ({ id: `pz${k}`, ...g, virtual: true }))]);
    const sps = computeSpaces(tmp, [], [], []);
    mine.forEach((comp) => {
      const P = { x: comp.at.px, y: comp.at.py };
      const sp = sps.find((q) => spaceContains(q, P.x, P.y));
      if (!sp) return;
      const loops = edgeLoops(spaceEdges(piecesOf(sp))).filter((l) => l.closed && l.segs.length >= 3);
      if (!loops.length) return;
      const polys = loops.map((l) => l.segs.map((g) => [g.p[0], g.p[1]]));
      polys.sort((p, q) => polyArea(q) - polyArea(p));
      const poly = simplifyClosed(polys[0], 2).map(([x, y]) => [Math.round(x * 10) / 10, Math.round(y * 10) / 10]);
      if (poly.length < 3 || polyArea(poly) < 3000) return;
      res.zones.push({ name: comp.name, poly, P });
    });
  });
  return res;
}

// همسایه‌های چهارطرفِ یک خانه (همان فضا)
export function cellNeighbors(model, cell) {
  const m = model.spaces.find((q) => q.s === cell.s);
  if (!m) return [];
  const out = [];
  [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ].forEach(([di, dj]) => {
    const q = m.grid[cell.i + di] && m.grid[cell.i + di][cell.j + dj];
    if (q) out.push(q);
  });
  return out;
}

// هر قسمت باید یک‌تکه باشد: در فضاهای touched، از هر اسم فقط بزرگ‌ترین تکه می‌ماند. خروجی: اسم‌هایی که تکه‌ی
// جدا افتاده‌شان پاک شد (labels در جا عوض می‌شود)
export function keepLargestPieces(model, labels, touchedSpaces, anchors = null) {
  const dropped = new Set();
  const comps = paintComponents(model, labels).filter((c) => touchedSpaces.has(c.s));
  const best = new Map();
  comps.forEach((c) => {
    const k = `${c.s}|${c.name}`;
    // تکه‌ای که خانه‌ی شروعِ این قسمت (anchor) در آن است می‌ماند؛ نبود → بزرگ‌ترین
    const anchored = anchors && anchors[c.name] && c.cells.some((q) => q.id === anchors[c.name]);
    const cur = best.get(k);
    if (!cur || (anchored && !cur.anchored) || (anchored === Boolean(cur.anchored) && cur.area < c.area)) best.set(k, { ...c, anchored, ref: c });
  });
  comps.forEach((c) => {
    if (best.get(`${c.s}|${c.name}`).ref === c) return;
    c.cells.forEach((q) => delete labels[q.id]);
    dropped.add(c.name);
  });
  return [...dropped];
}

// کاربر خانه‌ای دور از قسمتِ رنگ‌شده زد: کوتاه‌ترین راهِ خانه‌های خالی (همان فضا) تا خودِ قسمت — همه‌اش رنگ
// می‌شود تا قسمت یک‌تکه بماند. خروجی: خانه‌های بینِ راه (بدونِ خودِ cell) یا null اگر راهی نیست (فضای دیگر/
// قسمت‌های دیگر جلویش را گرفته‌اند). اول از راهِ صاف (افقی/عمودی) ترجیح داده می‌شود.
export function connectPath(model, labels, cell, name) {
  const m = model.spaces.find((q) => q.s === cell.s);
  if (!m) return null;
  const key = (c) => `${c.i},${c.j}`;
  const prev = new Map([[key(cell), null]]);
  const queue = [cell];
  const dirs = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];
  for (let h = 0; h < queue.length; h += 1) {
    const c = queue[h];
    const from = prev.get(key(c));
    // همان جهتِ قبلی اول (راهِ کم‌پیچ)
    const order = from ? [...dirs].sort((a, b) => (a[0] === c.i - from.i && a[1] === c.j - from.j ? -1 : 0) - (b[0] === c.i - from.i && b[1] === c.j - from.j ? -1 : 0)) : dirs;
    for (const [di, dj] of order) {
      const q = m.grid[c.i + di] && m.grid[c.i + di][c.j + dj];
      if (!q || prev.has(key(q))) continue;
      if (labels[q.id] === name) {
        const path = [];
        let t = c;
        while (t && t !== cell) {
          path.push(t);
          t = prev.get(key(t));
        }
        return path;
      }
      if (labels[q.id]) continue;
      prev.set(key(q), c);
      queue.push(q);
    }
  }
  return null;
}

// رنگ‌ها را از خانه‌بندیِ قبلی به خانه‌بندیِ تازه (اندازه‌ی دیگر) می‌برد: هر خانه‌ی تازه رنگِ «بیشترِ» سطحش را
// می‌گیرد (بی‌رنگ هم یک رأی است)، بعد هر قسمت یک‌تکه می‌ماند
export function remapLabels(oldModel, oldLabels, newModel) {
  const out = {};
  newModel.spaces.forEach((m) =>
    m.cells.forEach((c) => {
      const votes = new Map();
      for (let a = 0; a < 4; a += 1) {
        for (let b = 0; b < 4; b += 1) {
          const x = c.x1 + ((a + 0.5) / 4) * (c.x2 - c.x1);
          const y = c.y1 + ((b + 0.5) / 4) * (c.y2 - c.y1);
          if (!spaceContains(m.sp, x, y, 0)) continue;
          const o = findPaintCell(oldModel, x, y);
          const k = (o && oldLabels[o.id]) || '';
          votes.set(k, (votes.get(k) || 0) + 1);
        }
      }
      let best = '';
      let bn = -1;
      votes.forEach((n, k) => {
        if (n > bn) {
          bn = n;
          best = k;
        }
      });
      if (best) out[c.id] = best;
    })
  );
  keepLargestPieces(newModel, out, new Set(newModel.spaces.map((m) => m.s)));
  return out;
}
